import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Code, Palette, MapPin, MousePointerClick, TrendingUp, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from '@/components/Breadcrumbs';

// Image imports for optimisation
import aiNeuralNetwork from '@/assets/insights/ai-neural-network.jpg';
import fiberOpticsSpeed from '@/assets/insights/fiber-optics-speed.jpg';
import codeScreenDark from '@/assets/insights/code-screen-dark.jpg';
import assistiveTech from '@/assets/insights/assistive-tech.jpg';
import voiceAssistant from '@/assets/insights/voice-assistant.jpg';
import smartphoneDark from '@/assets/insights/smartphone-dark.jpg';
import neonStars from '@/assets/insights/neon-stars.jpg';
import futuristicChess from '@/assets/insights/futuristic-chess.jpg';

const baseUrl = 'https://smartsite-lead-gen.lovable.app';

// Featured Article Data
const featuredArticle = {
  slug: 'blog-post-master',
  tag: '2026 Trend',
  title: 'Why SEO is Dead: How to Rank in the Age of ChatGPT.',
  excerpt: 'The old way of ranking is over. In 2026, your site needs to speak "Machine Language." Here is how we code for AI.',
  image: aiNeuralNetwork,
};

// Article Grid Data
const articleGrid = [
  {
    slug: 'blog-speed',
    title: 'The 0.5-Second Rule',
    excerpt: 'Why Google penalises slow sites and why React architecture is the only solution.',
    tag: 'Performance',
    icon: Zap,
    image: fiberOpticsSpeed,
  },
  {
    slug: 'blog-platforms',
    title: 'Wix vs. Custom Code',
    excerpt: 'The true cost of "Drag and Drop." Why templates are killing your conversion rate.',
    tag: 'Tech Stack',
    icon: Code,
    image: codeScreenDark,
  },
  {
    slug: 'blog-accessibility',
    title: 'The Hidden Market (Accessibility)',
    excerpt: 'How accessible design opens your business to the 20% of users competitors ignore.',
    tag: 'Design',
    icon: Palette,
    image: assistiveTech,
  },
  {
    slug: 'blog-localseo',
    title: 'Hey Siri, Find a Plumber',
    excerpt: 'Optimising your Local SEO for the rise of Voice Search and Smart Speakers.',
    tag: 'Local SEO',
    icon: MapPin,
    image: voiceAssistant,
  },
  {
    slug: 'blog-mobile',
    title: 'Stop Losing Leads on Mobile',
    excerpt: 'Why "One-Touch" action buttons are the highest ROI feature for trades.',
    tag: 'Conversion',
    icon: MousePointerClick,
    image: smartphoneDark,
  },
  {
    slug: 'blog-reviews',
    title: 'Digital Trust: Automate Reviews',
    excerpt: 'How automated review systems build 5-star reputations while you sleep.',
    tag: 'Social Proof',
    icon: Star,
    image: neonStars,
  },
  {
    slug: 'blog-future',
    title: 'The 2026 Trade Landscape',
    excerpt: 'Digital or Die. How automation is separating the big players from the rest.',
    tag: 'Strategy',
    icon: TrendingUp,
    image: futuristicChess,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

// SEO Meta
const pageTitle = "Insights for South Wales Trades | SmartSite Solutions";
const pageDescription = "Expert insights on local SEO, website speed, AI search optimisation and lead generation for plumbers, builders and trades in South Wales.";

export default function Insights() {
  // CollectionPage/ItemList Schema
  const insightsSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "SmartSite Solutions Insights",
    "description": "Expert insights on AI, speed, and conversion for the modern trade.",
    "url": `${baseUrl}/insights`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": articleGrid.length + 1,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "url": `${baseUrl}/${featuredArticle.slug}`,
          "name": featuredArticle.title
        },
        ...articleGrid.map((article, index) => ({
          "@type": "ListItem",
          "position": index + 2,
          "url": `${baseUrl}/${article.slug}`,
          "name": article.title
        }))
      ]
    }
  }), []);

  // Set page title and meta
  useEffect(() => {
    // Add schema
    let scriptTag = document.querySelector('script[data-schema="insights"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-schema', 'insights');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(insightsSchema);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="insights"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [insightsSchema]);

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Insights', href: '/insights' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/insights" />
      </Helmet>
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Breadcrumbs */}
          <Breadcrumbs items={breadcrumbItems} />

          {/* Section 1: The Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto mb-20"
          >
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
              Future-Proofing{' '}
              <span className="text-gradient">Local Business.</span>
            </h1>
            <p className="text-xl text-muted-foreground font-light">
              Insights on AI, Speed, and Conversion for the Modern Trade.
            </p>
          </motion.header>

          {/* Section 2: Featured Article (Hero Card) */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-20"
          >
            <Link to={`/${featuredArticle.slug}`} className="group block">
              <div className="glass-card overflow-hidden transition-all duration-500 hover:border-white/20 hover:bg-white/[0.08] group-hover:shadow-2xl">
                <div className="grid lg:grid-cols-2">
                  {/* Image Left */}
                  <div className="aspect-[4/3] lg:aspect-auto relative overflow-hidden">
                    <img 
                      src={featuredArticle.image} 
                      alt="Abstract AI neural network visualisation"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {/* Dark overlay for text readability */}
                    <div className="absolute inset-0 bg-black/60" />
                    {/* AI Text overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="font-display text-6xl lg:text-8xl font-bold text-gradient opacity-60">
                        AI
                      </span>
                    </div>
                  </div>
                  
                  {/* Text Right */}
                  <div className="p-6 md:p-8 lg:p-12 flex flex-col justify-center">
                    <span className="inline-block w-fit px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-6">
                      {featuredArticle.tag}
                    </span>
                    
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 leading-tight group-hover:text-gradient transition-all duration-300">
                      {featuredArticle.title}
                    </h2>
                    
                    <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                      {featuredArticle.excerpt}
                    </p>
                    
                    <div>
                      <span className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold text-sm group-hover:from-cyan-400 group-hover:to-blue-500 transition-all duration-300">
                        Read Deep Dive
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.section>

          {/* Section 3: The Article Grid */}
          <motion.section
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mb-20"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {articleGrid.map((article) => {
                const IconComponent = article.icon;
                return (
                  <motion.article key={article.slug} variants={cardVariants}>
                    <Link to={`/${article.slug}`} className="group block h-full">
                      <div className="glass-card h-full overflow-hidden transition-all duration-500 hover:border-white/20 hover:bg-white/[0.08] group-hover:shadow-xl flex flex-col">
                        {/* Image */}
                        <div className="aspect-[16/9] relative overflow-hidden">
                          <img 
                            src={article.image} 
                            alt={article.title}
                            loading="lazy"
                            decoding="async"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          {/* Dark overlay */}
                          <div className="absolute inset-0 bg-black/60" />
                          {/* Icon overlay */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="p-4 rounded-2xl bg-orange-500/10 backdrop-blur-sm border border-orange-500/20">
                              <IconComponent className="w-8 h-8 text-orange-400" />
                            </div>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-5 md:p-6 flex flex-col flex-grow">
                          {/* Tag - Standardized Orange Pill */}
                          <div className="flex items-center gap-3 mb-4">
                            <span className="px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-medium">
                              {article.tag}
                            </span>
                          </div>
                          
                          {/* Title */}
                          <h3 className="font-display text-xl font-bold mb-3 leading-tight group-hover:text-gradient transition-all duration-300">
                            {article.title}
                          </h3>
                          
                          {/* Excerpt */}
                          <p className="text-muted-foreground text-sm leading-relaxed flex-grow">
                            {article.excerpt}
                          </p>
                          
                          {/* Read More */}
                          <div className="mt-5 flex items-center text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            Read article
                            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                );
              })}
            </div>
          </motion.section>

          {/* Section 4: Newsletter Lead Magnet (Slim Glass Banner) */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="glass-card p-5 md:p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <p className="text-lg font-medium text-slate-200">
                  Don't let the tech leave you behind.{' '}
                  <span className="text-muted-foreground font-normal">Get monthly insights delivered.</span>
                </p>
                
                <form className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                  <Input
                    type="email"
                    placeholder="Email Address"
                    className="sm:w-64 bg-white/5 border-white/10 focus:border-cyan-500/50"
                  />
                  <Button className="gradient-button whitespace-nowrap">
                    Subscribe
                  </Button>
                </form>
              </div>
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
